import { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart } from 'recharts';
import { Activity, Zap, TrendingUp, Calendar as CalendarIcon, Settings, Users, LogOut, RefreshCw, Download, Clock, Mail, BarChart3, Edit, Trash2, Upload, WifiOff, Wifi } from 'lucide-react';
import { formatDateTime, formatDate } from './dateUtils';
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card } from "./components/ui/card";
import { Toaster, toast } from "./components/ui/sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "./components/ui/select";
import { RadioGroup, RadioGroupItem } from "./components/ui/radio-group";
import { Popover, PopoverTrigger, PopoverContent } from "./components/ui/popover";
import { Calendar } from "./components/ui/calendar";
import { Switch } from "./components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "./components/ui/dialog";
import { Label } from "./components/ui/label";
import InstallPrompt from "./components/InstallPrompt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Chart style constants - extracted to avoid unnecessary re-renders
const CHART_MARGIN = { top: 10, right: 30, left: 0, bottom: 0 };
const AXIS_TICK_STYLE = { fontSize: 12 };
const AXIS_TICKLINE_STYLE = { stroke: '#9ca3af' };
const AXIS_STROKE_COLOR = '#6b7280';
const GRID_STROKE_COLOR = '#e5e7eb';
const TOOLTIP_STYLE = { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' };

function useApi() {
  const client = useMemo(() => {
    const c = axios.create({ baseURL: API, withCredentials: true });
    return c;
  }, []);
  return client;
}

function formatRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatRuntime(seconds) {
  if (!seconds || seconds === 0) return "0h 0m";
  
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
}

// Professional Chart Component with Axes
function ProChart({ points, color = "#3b82f6", unit = "kW", showGrid = true, timeRange = "24h" }) {
  const chartData = useMemo(() => {
    if (!points || points.length === 0) return [];
    // Sort by timestamp to ensure chronological order (oldest to newest, left to right)
    const sortedPoints = [...points].sort((a, b) => 
      new Date(a.ts).getTime() - new Date(b.ts).getTime()
    );
    
    // Format time label based on the time range
    return sortedPoints.map((p) => {
      const date = new Date(p.ts);
      let timeLabel;
      
      if (timeRange === "7d" || timeRange === "30d") {
        // For 7 days and 30 days, show date (e.g., "Dec 06" or "Mon 06")
        timeLabel = date.toLocaleDateString('en-GB', { month: 'short', day: '2-digit' });
      } else {
        // For 1h and 24h, show time (HH:mm)
        timeLabel = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
      }
      
      // Convert to kW if unit is kW
      const rawValue = Number(p.power ?? p.kWh ?? 0);
      const displayValue = unit === "kW" ? rawValue / 1000 : rawValue;
      
      return {
        time: timeLabel,
        timestamp: date.getTime(),
        value: displayValue,
      };
    });
  }, [points, timeRange, unit]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3">
          <p className="text-xs text-gray-500">{new Date(data.timestamp).toLocaleString('en-GB', { hour12: false })}</p>
          <p className="text-sm font-semibold text-gray-900">{data.value.toFixed(2)} {unit}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full" data-testid="pro-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={CHART_MARGIN}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE_COLOR} />}
          <XAxis 
            dataKey="time" 
            stroke={AXIS_STROKE_COLOR}
            tick={AXIS_TICK_STYLE}
            tickLine={AXIS_TICKLINE_STYLE}
            interval={(timeRange === "7d" || timeRange === "30d") ? "preserveStartEnd" : "auto"}
          />
          <YAxis 
            stroke={AXIS_STROKE_COLOR}
            tick={AXIS_TICK_STYLE}
            tickLine={AXIS_TICKLINE_STYLE}
            label={{ value: unit, angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#6b7280' } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <defs>
            <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={color} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <Area 
            type="monotone" 
            dataKey="value" 
            stroke={color} 
            strokeWidth={2}
            fill={`url(#gradient-${color})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// Compact Sparkline for device cards
function Sparkline({ points, width = 220, height = 64 }) {
  const last = points && points.length ? points[points.length - 1] : null;
  const avg = points && points.length ? Math.round(points.reduce((a,b)=>a+(Number(b.power||b.kWh||0)),0)/points.length) : 0;
  const max = points && points.length ? Math.round(Math.max(...points.map(p=>Number(p.power||p.kWh||0)))) : 0;
  
  const chartData = useMemo(() => {
    if (!points || points.length === 0) return [];
    return points.map((p) => ({
      value: Number(p.power ?? p.kWh ?? 0),
    }));
  }, [points]);

  return (
    <div data-testid="device-sparkline">
      <ResponsiveContainer width={width} height={height}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="sparkGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <Area 
            type="monotone" 
            dataKey="value" 
            stroke="#10b981" 
            strokeWidth={2}
            fill="url(#sparkGradient)"
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
        <span className="inline-flex items-center gap-1">
          <Zap className="w-3 h-3 text-emerald-600" />
          <span className="font-medium">avg {avg}W</span>
        </span>
        <span>max {max}W</span>
        {last && <span>last {Math.round(last.power || last.kWh || 0)}W</span>}
      </div>
    </div>
  );
}

function DateTimePicker({ label, value, onChange, testid }) {
  const [time, setTime] = useState(value ? `${String(new Date(value).getHours()).padStart(2, '0')}:${String(new Date(value).getMinutes()).padStart(2, '0')}` : "00:00");
  
  const handleDateChange = (newDate) => {
    if (newDate) {
      const [hours, minutes] = time.split(':').map(Number);
      const d = new Date(newDate);
      d.setHours(hours, minutes, 0, 0);
      onChange(d);
    }
  };
  
  const handleTimeChange = (newTime) => {
    setTime(newTime);
    if (value) {
      const [hours, minutes] = newTime.split(':').map(Number);
      const d = new Date(value);
      d.setHours(hours, minutes, 0, 0);
      onChange(d);
    }
  };
  
  const display = value ? formatDate(value) : "Pick date";
  
  return (
    <div className="space-y-2">
      {label && <div className="text-sm font-medium text-gray-700 mb-1">{label}</div>}
      <div className="space-y-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start text-sm h-10" data-testid={(testid||'date')+"-trigger"}>
              <CalendarIcon className="w-4 h-4 mr-2" />
              {display}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" data-testid={(testid||'date')+"-content"}>
            <Calendar mode="single" selected={value} onSelect={handleDateChange} initialFocus />
          </PopoverContent>
        </Popover>
        <div className="flex items-center gap-2 border rounded-md px-3 py-2 bg-white h-10">
          <Clock className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <Input
            type="time"
            value={time}
            onChange={(e) => handleTimeChange(e.target.value)}
            className="w-full border-0 p-0 h-auto text-sm focus-visible:ring-0"
            data-testid={(testid||'date')+"-time"}
            step="60"
          />
        </div>
        {value && (
          <div className="text-xs text-gray-600 bg-blue-50 px-2 py-1 rounded">
            📅 {formatDateTime(value)}
          </div>
        )}
      </div>
    </div>
  );
}

function useSettings() {
  const api = useApi();
  const [settings, setSettings] = useState({ offlineThresholdMinutes: 2 });
  useEffect(() => {
    (async () => {
      try { 
        const res = await api.get('/settings'); 
        setSettings(res.data); 
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    })();
  }, [api]);
  return settings;
}

function LogoutButton() {
  const api = useApi();
  const navigate = useNavigate();
  const onLogout = async () => {
    try { 
      await api.post('/auth/logout'); 
    } catch (err) {
      console.error('Logout error:', err);
    }
    navigate('/');
    window.location.reload();
  };
  return (
    <Button size="sm" variant="outline" onClick={onLogout} data-testid="logout-button" className="gap-2">
      <LogOut className="w-4 h-4" />
      Log out
    </Button>
  );
}

function ConversionQuickStart({ open, onOpenChange, deviceId }) {
  const api = useApi();
  const navigate = useNavigate();
  const handleStart = () => {
    onOpenChange(false);
    navigate(`/conversions?device=${deviceId}`);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quick-start-dialog">
        <DialogHeader>
          <DialogTitle>Start Conversion for {deviceId}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">Navigate to the Conversions page to create a new energy comparison for this device.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleStart} data-testid="quick-start-go">Go to Conversions</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Login({ onLoggedIn }) {
  const api = useApi();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const loginRes = await api.post("/auth/login", { email, password });
      console.log('Login response:', loginRes);
      toast.success("Logged in");
      // Small delay to ensure cookie is set
      await new Promise(resolve => setTimeout(resolve, 100));
      onLoggedIn();
    } catch (e) {
      console.error('Login error:', e);
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  }, [api, email, password, onLoggedIn]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50">
      <div className="max-w-md mx-auto pt-32 px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-600 mb-6 shadow-lg">
            <Activity className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-5xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">DST Energy</h1>
          <p className="text-gray-600 mt-3">Sign in to view energy dashboards</p>
        </div>
        <Card className="p-8 shadow-xl border-gray-200">
          <form className="space-y-5" onSubmit={submit} data-testid="login-form">
            <div>
              <label className="text-sm font-semibold text-gray-700">Email</label>
              <Input 
                data-testid="login-email-input" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="you@company.com"
                className="mt-1.5"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">Password</label>
              <Input 
                data-testid="login-password-input" 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                placeholder="••••••••"
                className="mt-1.5"
              />
            </div>
            <Button data-testid="login-form-submit-button" disabled={loading} type="submit" className="w-full h-11 font-semibold">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Forgot your password?
            </Link>
          </div>
        </Card>
      </div>
      <Toaster />
    </div>
  );
}

function DeviceCard({ device }) {
  const [series, setSeries] = useState([]);
  const [quickOpen, setQuickOpen] = useState(false);
  const api = useApi();
  const settings = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const now = new Date();
        const ago24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const body = {
          deviceIds: [device.deviceId],
          phases: [0],
          range: { start: ago24h.toISOString(), end: now.toISOString() },
          interval: "1m",
        };
        const res = await api.post("/readings/query", body);
        const data = res.data[device.deviceId]?.[0] || [];
        setSeries(data);
      } catch (err) {
        console.error('Failed to load device series data:', err);
      }
    })();
  }, [api, device.deviceId]);

  const connectionStatus = useMemo(() => {
    if (!device.lastSeen) return { online: false, warning: false, timeLeft: null };
    const diff = Date.now() - new Date(device.lastSeen).getTime();
    const threshold = (settings.offlineThresholdMinutes || 2) * 60 * 1000;
    const warningThreshold = threshold * 0.75; // Warn at 75% of threshold
    const online = diff < threshold;
    const warning = online && diff > warningThreshold;
    const timeLeft = online ? Math.max(0, Math.floor((threshold - diff) / 1000 / 60)) : null;
    return { online, warning, timeLeft };
  }, [device.lastSeen, settings.offlineThresholdMinutes]);

  return (
    <>
      <Card className={`p-6 hover:shadow-xl transition-all duration-300 border-2 bg-white ${
        !connectionStatus.online ? 'border-red-300 bg-red-50/30' : 
        connectionStatus.warning ? 'border-yellow-300 bg-yellow-50/30' : 
        'border-gray-200'
      }`} data-testid={`device-card-${device.deviceId}`}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-bold text-gray-900">{device.deviceId}</h3>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                connectionStatus.online ? 
                  (connectionStatus.warning ? 'bg-yellow-100 text-yellow-800' : 'bg-emerald-100 text-emerald-800') : 
                  'bg-red-100 text-red-800'
              }`}>
                {connectionStatus.online ? (connectionStatus.warning ? 'Warning' : 'Online') : 'Offline'}
              </span>
            </div>
            <p className="text-sm text-gray-500">Last seen: {formatRelative(device.lastSeen)}</p>
            {connectionStatus.warning && (
              <p className="text-xs text-yellow-700 mt-1 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Will be offline in ~{connectionStatus.timeLeft}m
              </p>
            )}
            {!connectionStatus.online && (
              <p className="text-xs text-red-700 mt-1 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Offline for {Math.floor((Date.now() - new Date(device.lastSeen).getTime()) / 1000 / 60)}m (threshold: {settings.offlineThresholdMinutes || 10}m)
              </p>
            )}
            {device.totalRuntimeSeconds > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-600 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  <span className="font-medium">Total runtime:</span> {formatRuntime(device.totalRuntimeSeconds)}
                </p>
                {device.currentSessionSeconds > 0 && (
                  <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                    <Zap className="w-3 h-3" />
                    <span className="font-medium">Current session:</span> {formatRuntime(device.currentSessionSeconds)}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-gray-900">{((device.currentTotalPower || 0) / 1000).toFixed(2)}</div>
            <div className="text-xs text-gray-500 font-medium">kW</div>
          </div>
        </div>
        
        <div className="mb-4 pb-1">
          <Sparkline points={series} width={280} height={60} />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`/device/${device.deviceId}`)} className="flex-1" data-testid={`device-view-${device.deviceId}`}>
            View Details
          </Button>
          <Button size="sm" onClick={() => setQuickOpen(true)} className="flex-1" data-testid={`device-campaign-${device.deviceId}`}>
            Start Conversion
          </Button>
        </div>
      </Card>
      <ConversionQuickStart open={quickOpen} onOpenChange={setQuickOpen} deviceId={device.deviceId} />
    </>
  );
}

function Overview() {
  const [devices, setDevices] = useState([]);
  const api = useApi();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/devices/overview");
        setDevices(res.data || []);
      } catch (err) {
        console.error('Failed to load devices overview:', err);
      }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [api]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <header className="border-b bg-white/80 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-blue-600 to-emerald-600 flex items-center justify-center flex-shrink-0">
                <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">DST Energy Monitor</h1>
                <p className="text-xs text-gray-500 hidden sm:block">Real-time device monitoring</p>
              </div>
            </Link>
            <nav className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <Link className="hidden md:block text-sm font-medium text-blue-600" to="/" data-testid="nav-overview-link">Overview</Link>
              <Link className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors" to="/conversions" data-testid="nav-conversions-link">Conversions</Link>
              <Link className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors" to="/admin" data-testid="nav-admin-link">Admin</Link>
              <Link className="md:hidden text-gray-700 hover:text-blue-600" to="/conversions" title="Conversions">
                <CalendarIcon className="w-5 h-5" />
              </Link>
              <Link className="md:hidden text-gray-700 hover:text-blue-600" to="/admin" title="Admin">
                <Settings className="w-5 h-5" />
              </Link>
              <LogoutButton />
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8" data-testid="overview-page">
        <div className="mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Active Devices</h2>
          <p className="text-sm sm:text-base text-gray-600">Monitor your energy consumption in real-time</p>
        </div>
        
        {devices.length === 0 ? (
          <Card className="p-12 text-center">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No devices found. Devices will appear once they start publishing data.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {devices.map((d) => <DeviceCard key={d.deviceId} device={d} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function DeviceDetail() {
  const { deviceId } = useParams();
  const [data, setData] = useState([]);
  const [device, setDevice] = useState(null);
  const [range, setRange] = useState("24h");
  const [interval, setInterval] = useState("1m");
  const phase = 0; // Always show total (phase 0) only
  const [live, setLive] = useState(false);
  const [kpis, setKpis] = useState({ avg: 0, max: 0 });
  const [uptime, setUptime] = useState({ h1: 0, h24: 0, d7: 0, d30: 0 });
  const [offlineData, setOfflineData] = useState(null);
  const [deviceStats, setDeviceStats] = useState(null);
  const api = useApi();
  const settings = useSettings();

  // Load cached device stats (fast - pre-computed on backend)
  const loadDeviceStats = useCallback(async () => {
    try {
      const res = await api.get(`/devices/${deviceId}/stats`);
      setDeviceStats(res.data);
      // Use cached uptime values
      if (res.data?.uptime) {
        setUptime(res.data.uptime);
      }
    } catch (err) {
      console.error('Failed to load device stats:', err);
    }
  }, [api, deviceId]);

  // Get days based on range selection
  const getOfflineDays = useCallback(() => {
    if (range === "1h") return 1;
    if (range === "24h") return 1;
    if (range === "7d") return 7;
    if (range === "30d") return 30;
    return 7;
  }, [range]);

  // Fetch offline periods data
  const loadOfflinePeriods = useCallback(async () => {
    try {
      const days = getOfflineDays();
      const res = await api.get(`/devices/${deviceId}/offline-periods?days=${days}`);
      setOfflineData(res.data);
    } catch (err) {
      console.error('Failed to load offline periods:', err);
    }
  }, [api, deviceId, getOfflineDays]);

  const load = useCallback(async () => {
    try {
      const now = new Date();
      let start;
      let effectiveInterval = interval;
      
      if (range === "1h") {
        start = new Date(now.getTime() - 60 * 60 * 1000);
        // Use user-selected interval for 1h (usually raw or 1m)
      } else if (range === "24h") {
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        // Force 1m interval for 24h to get reasonable number of data points
        effectiveInterval = "1m";
      } else if (range === "7d") {
        // Last 7 days
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        // Force 1h interval for 7 days to avoid too many data points
        effectiveInterval = "1h";
      } else {
        // Last 30 days
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        // Force 1d interval for 30 days to keep data points reasonable
        effectiveInterval = "1d";
      }

      const body = {
        deviceIds: [deviceId],
        phases: [phase],
        range: { start: start.toISOString(), end: now.toISOString() },
        interval: effectiveInterval,
      };
      const res = await api.post("/readings/query", body);
      const arr = res.data[deviceId]?.[phase] || [];
      setData(arr);

      const vals = arr.map(x => Number(x.power || x.kWh || 0));
      setKpis({
        avg: vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0,
        max: vals.length ? Math.round(Math.max(...vals)) : 0,
      });

      // Fetch device info with runtime metrics
      const devRes = await api.get(`/devices/${deviceId}/info`);
      setDevice(devRes.data);

      // Note: Uptime stats are loaded separately via loadDeviceStats() for better performance
    } catch (err) {
      console.error('Failed to load device data:', err);
    }
  }, [api, deviceId, range, interval, phase]);

  // Load data when dependencies change
  useEffect(() => {
    load();
    loadDeviceStats(); // Fast - uses cached backend data
    loadOfflinePeriods();
  }, [deviceId, range, interval, phase, load, loadDeviceStats, loadOfflinePeriods]);

  // Live mode - auto refresh every 10 seconds
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => {
      load();
    }, 10000);
    return () => clearInterval(timer);
  }, [live, load]);

  const isOnline = useMemo(() => {
    if (!device?.lastSeen) return false;
    const diff = Date.now() - new Date(device.lastSeen).getTime();
    return diff < (settings.offlineThresholdMinutes || 2) * 60 * 1000;
  }, [device, settings.offlineThresholdMinutes]);

  // Format duration in a human-readable way
  const formatDuration = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)}m`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${Math.round(minutes % 60)}m`;
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    return `${days}d ${hours}h`;
  };

  const exportCsv = () => {
    const rows = ["timestamp,value"];
    data.forEach(d => rows.push(`${d.ts},${d.power || d.kWh || 0}`));
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${deviceId}_phase${phase}_${range}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <header className="border-b bg-white/80 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-blue-600 to-emerald-600 flex items-center justify-center flex-shrink-0">
                <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">{deviceId}</h1>
                <p className="text-xs text-gray-500 hidden sm:block">Device details & analytics</p>
              </div>
            </Link>
            <nav className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <Link className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors" to="/" data-testid="nav-overview-link">Overview</Link>
              <Link className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors" to="/conversions" data-testid="nav-conversions-link">Conversions</Link>
              <Link className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors" to="/admin" data-testid="nav-admin-link">Admin</Link>
              <Link className="md:hidden text-gray-700 hover:text-blue-600" to="/" title="Overview">
                <BarChart3 className="w-5 h-5" />
              </Link>
              <LogoutButton />
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8" data-testid="device-detail-page">
        {/* Title section */}
        <div className="mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{deviceId} Power Analysis</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isOnline ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'}`}>
              {isOnline ? 'Online' : 'Offline'}
            </span>
            <span className="text-xs sm:text-sm text-gray-500">Last seen: {formatRelative(device?.lastSeen)}</span>
          </div>
        </div>
          
        {/* Controls section - now stacked on mobile */}
        <div className="mb-4 sm:mb-6 space-y-3">
          {/* Time range and interval selectors */}
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={range} onValueChange={setRange} data-testid="range-selector">
              <SelectTrigger className="w-36 sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">Last 1 hour</SelectItem>
                <SelectItem value="24h">Last 24 hours</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
              </SelectContent>
            </Select>

            <Select value={interval} onValueChange={setInterval} data-testid="interval-selector">
              <SelectTrigger className="w-28 sm:w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="raw">Raw</SelectItem>
                <SelectItem value="1m">1 minute</SelectItem>
                <SelectItem value="1h">1 hour</SelectItem>
                <SelectItem value="1d">1 day</SelectItem>
              </SelectContent>
            </Select>

            <div className={`flex items-center gap-2 px-2 sm:px-3 py-2 border rounded-lg transition-colors ${live ? 'bg-green-50 border-green-500' : 'bg-white'}`}>
              <Switch checked={live} onCheckedChange={setLive} data-testid="live-toggle" />
              <span className={`text-xs sm:text-sm font-medium whitespace-nowrap ${live ? 'text-green-700' : 'text-gray-700'}`}>
                {live ? '🟢 Live' : 'Live'}
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} data-testid="refresh-button" className="flex-1 sm:flex-none">
              <RefreshCw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>

            <Button variant="outline" onClick={exportCsv} data-testid="export-button" className="flex-1 sm:flex-none">
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Export CSV</span>
            </Button>
          </div>
        </div>

        {/* Phase tabs removed - always showing total power only */}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
          <Card className="p-6 col-span-1 lg:col-span-3 bg-white border-gray-200">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Power Consumption</h3>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Zap className="w-4 h-4" />
                <span>{data.length} data points</span>
              </div>
            </div>
            <div className="h-80">
              <ProChart points={data} color={settings.deviceColors?.[deviceId] || "#3b82f6"} unit="kW" timeRange={range} />
            </div>
          </Card>

          <div className="space-y-4">
            <Card className="p-6 bg-white border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <h3 className="text-sm font-semibold text-gray-700">Key Metrics</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Average Power</p>
                  <p className="text-2xl font-bold text-gray-900" data-testid="kpi-avg">{(kpis.avg / 1000).toFixed(2)} kW</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Max Power</p>
                  <p className="text-2xl font-bold text-gray-900" data-testid="kpi-max">{(kpis.max / 1000).toFixed(2)} kW</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-5 h-5 text-emerald-600" />
                <h3 className="text-sm font-semibold text-gray-700">Device Health</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Uptime (1h)</p>
                  <p className="text-lg font-bold text-gray-900">{uptime.h1}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Uptime (24h)</p>
                  <p className="text-lg font-bold text-gray-900">{uptime.h24}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Uptime (7d)</p>
                  <p className="text-lg font-bold text-gray-900">{uptime.d7}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Uptime (30d)</p>
                  <p className="text-lg font-bold text-gray-900">{uptime.d30}%</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6 bg-white border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-blue-600" />
                <h3 className="text-sm font-semibold text-gray-700">Runtime</h3>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Total Accumulated Runtime</p>
                <p className="text-2xl font-bold text-gray-900">{formatRuntime(device?.totalRuntimeSeconds)}</p>
                <p className="text-xs text-gray-400 mt-2">Time device has been online since first reading</p>
              </div>
            </Card>
          </div>
        </div>

        {/* Offline Periods Timeline */}
        {offlineData && (
          <Card className="p-6 bg-white border-gray-200 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <WifiOff className="w-5 h-5 text-orange-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Connection Status ({range === "1h" || range === "24h" ? "Last 24 Hours" : range === "7d" ? "Last 7 Days" : "Last 30 Days"})
                </h3>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                  Online {offlineData.uptimePercent}%
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-red-400"></span>
                  Offline {(100 - offlineData.uptimePercent).toFixed(1)}%
                </span>
              </div>
            </div>

            {/* Timeline Visualization - daily for long periods, hourly for short */}
            <div className="mb-6">
              <p className="text-xs text-gray-500 mb-2">
                {range === "30d" ? "Daily" : "Hourly"} Connection Timeline
              </p>
              <div className="flex flex-wrap gap-0.5">
                {range === "30d" ? (
                  // Daily view for 30-day period - aggregate timeline by day
                  (() => {
                    const dailyData = {};
                    offlineData.timeline?.forEach(hour => {
                      const date = new Date(hour.hour);
                      const dayKey = date.toISOString().split('T')[0];
                      if (!dailyData[dayKey]) {
                        dailyData[dayKey] = { online: 0, offline: 0, partial: 0, total: 0 };
                      }
                      dailyData[dayKey][hour.status]++;
                      dailyData[dayKey].total++;
                    });
                    
                    return Object.entries(dailyData).map(([day, data], idx) => {
                      const date = new Date(day);
                      const onlinePct = data.total > 0 ? (data.online / data.total) * 100 : 0;
                      const status = onlinePct >= 80 ? 'online' : onlinePct >= 20 ? 'partial' : 'offline';
                      
                      return (
                        <div key={day} className="relative group">
                          <div
                            className={`w-4 h-8 rounded-sm cursor-pointer transition-all hover:scale-110 ${
                              status === 'online' ? 'bg-emerald-500' :
                              status === 'partial' ? 'bg-yellow-400' :
                              'bg-red-400'
                            }`}
                            title={`${date.toLocaleDateString('en-GB')} - ${Math.round(onlinePct)}% online (${data.online}h online, ${data.offline}h offline)`}
                          />
                          {idx % 7 === 0 && (
                            <div className="absolute -bottom-4 left-0 text-xs text-gray-400 whitespace-nowrap">
                              {date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()
                ) : (
                  // Hourly view for shorter periods
                  offlineData.timeline?.map((hour, idx) => {
                    const date = new Date(hour.hour);
                    const isNewDay = idx === 0 || date.getHours() === 0;
                    return (
                      <div key={idx} className="relative group">
                        {isNewDay && idx > 0 && <div className="absolute -left-1 top-0 w-0.5 h-full bg-gray-300"></div>}
                        <div
                          className={`w-3 h-6 rounded-sm cursor-pointer transition-all hover:scale-110 ${
                            hour.status === 'online' ? 'bg-emerald-500' :
                            hour.status === 'partial' ? 'bg-yellow-400' :
                            'bg-red-400'
                          }`}
                          title={`${date.toLocaleDateString()} ${date.getHours()}:00 - ${hour.status} (${hour.readings} readings)`}
                        />
                        {isNewDay && (
                          <div className="absolute -bottom-4 left-0 text-xs text-gray-400 whitespace-nowrap">
                            {date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              <div className="flex items-center gap-4 mt-6 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500"></span> Online</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-yellow-400"></span> Partial</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-400"></span> Offline</span>
              </div>
            </div>

            {/* Offline Periods List */}
            {offlineData.offlinePeriods?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Offline Periods ({offlineData.offlinePeriods.length})</p>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {offlineData.offlinePeriods.map((period) => (
                    <div 
                      key={`${period.start}-${period.end || 'ongoing'}`} 
                      className={`flex items-center justify-between p-2 rounded-lg ${period.ongoing ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-2">
                        <WifiOff className={`w-4 h-4 ${period.ongoing ? 'text-red-500' : 'text-gray-400'}`} />
                        <div>
                          <p className="text-sm text-gray-700">
                            {new Date(period.start).toLocaleDateString('en-GB')} {new Date(period.start).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'})}
                            {' → '}
                            {period.ongoing ? 
                              <span className="text-red-600 font-medium">Now (ongoing)</span> : 
                              `${new Date(period.end).toLocaleDateString('en-GB')} ${new Date(period.end).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'})}`
                            }
                          </p>
                        </div>
                      </div>
                      <span className={`text-sm font-medium ${period.ongoing ? 'text-red-600' : 'text-gray-600'}`}>
                        {formatDuration(period.durationMinutes)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {offlineData.offlinePeriods?.length === 0 && (
              <div className="text-center py-4 text-emerald-600">
                <Wifi className="w-8 h-8 mx-auto mb-2" />
                <p className="font-medium">
                  No offline periods in the {range === "1h" || range === "24h" ? "last 24 hours" : range === "7d" ? "last 7 days" : "last 30 days"}!
                </p>
                <p className="text-sm text-gray-500">Device has been consistently online.</p>
              </div>
            )}
          </Card>
        )}
      </main>
    </div>
  );
}

function Conversions() {
  const api = useApi();
  const [searchParams] = useSearchParams();
  const preselect = searchParams.get('device') || '';
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [results, setResults] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(preselect);
  const [name, setName] = useState("New Conversion");
  const [beforeStart, setBeforeStart] = useState(new Date());
  const [beforeEnd, setBeforeEnd] = useState(new Date());
  const [afterStart, setAfterStart] = useState(new Date());
  const [afterEnd, setAfterEnd] = useState(new Date());
  const [overlay, setOverlay] = useState({ before: [], after: [] });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailRecipientName, setEmailRecipientName] = useState("");
  const [emailSenderName, setEmailSenderName] = useState("DST-CHEMICALS Team");
  const [emailMessage, setEmailMessage] = useState("Please find attached the comprehensive energy conversion analysis report.\n\nThis report contains detailed energy consumption data, comparative analysis, and daily summaries to support your energy optimization initiatives.\n\nBest regards,");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingConversion, setEditingConversion] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  const apiClient = useApi();
  
  // Check if user can edit/delete a conversion
  const canModifyConversion = useCallback((conversion) => {
    if (!userInfo) return false;
    const isAdmin = userInfo.role === 'admin';
    const isCreator = conversion.createdBy === userInfo.email;
    return isAdmin || isCreator;
  }, [userInfo]);
  
  const load = useCallback(async () => {
    try {
      const [resC, resD, resUser] = await Promise.all([
        apiClient.get("/conversions"),
        apiClient.get("/devices/overview"),
        apiClient.get("/me"),
      ]);
      setItems(resC.data || []);
      const dv = resD.data || [];
      setDevices(dv);
      setUserInfo(resUser.data || null);
      if (!preselect) setSelectedDevice(dv[0]?.deviceId || "");
    } catch (err) {
      console.error('Failed to load conversions data:', err);
    }
  }, [apiClient, preselect]);

  useEffect(() => { load(); }, [load]);

  // Fetch comparison data using unified endpoint - single source of truth
  const buildOverlay = useCallback(async (conversionId) => {
    try {
      console.log(`[Overlay] Fetching unified chart data for ${conversionId}`);
      const resp = await apiClient.get(`/conversions/${conversionId}/chart-data`);
      const chartData = resp.data;
      
      console.log(`[Overlay] Received data:`, {
        before: chartData.before?.data_points,
        after: chartData.after?.data_points,
        maxHours: chartData.chart?.max_hours
      });
      
      // Convert to the format expected by the chart
      const beforeData = chartData.before?.data?.map(d => ({ ts: d.ts, kWh: d.kWh })) || [];
      const afterData = chartData.after?.data?.map(d => ({ ts: d.ts, kWh: d.kWh })) || [];
      
      // Store the full chart data for direct use
      setOverlay({ 
        before: beforeData, 
        after: afterData,
        chartData: chartData.chart?.normalized_data || [],
        summary: chartData.summary
      });
      
      console.log(`[Overlay] Final - Before: ${beforeData.length}, After: ${afterData.length}`);
    } catch (err) {
      console.error('[Overlay] Error fetching chart data:', err);
      toast.error('Failed to load comparison data');
      setOverlay({ before: [], after: [], chartData: [], summary: null });
    }
  }, [apiClient]);

  const runCompare = useCallback(async (cid) => {
    setSelected(cid);
    setResults(null);
    // Reset overlay to prevent stale chart data from previous conversion
    setOverlay({ before: [], after: [], chartData: [], summary: null });
    try {
      const res = await apiClient.get(`/conversions/${cid}/compare`);
      setResults(res.data);
      // Use unified chart-data endpoint - pass conversion ID
      await buildOverlay(cid);
    } catch (e) { 
      console.error('Comparison failed:', e);
      toast.error("Comparison failed"); 
    }
  }, [apiClient, buildOverlay]);

  const submitConversion = useCallback(async () => {
    if (!selectedDevice) return toast.error("Select a device");
    const body = {
      siteId: "SITE-ACME-A",
      name,
      devices: [selectedDevice],
      before: { start: new Date(beforeStart).toISOString(), end: new Date(beforeEnd).toISOString() },
      after: { start: new Date(afterStart).toISOString(), end: new Date(afterEnd).toISOString() },
      notes: "",
    };
    try {
      const res = await apiClient.post("/conversions", body);
      toast.success("Conversion created");
      setItems((prev) => [res.data, ...prev]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  }, [apiClient, name, selectedDevice, beforeStart, beforeEnd, afterStart, afterEnd]);

  const exportOverlayCsv = () => {
    const keys = new Set([...overlay.before.map(x=>x.ts), ...overlay.after.map(x=>x.ts)]);
    const rows = ["bucket_ts,before_kWh,after_kWh"];
    Array.from(keys).sort().forEach((ts) => {
      const b = overlay.before.find((x) => x.ts === ts)?.kWh ?? "";
      const a = overlay.after.find((x) => x.ts === ts)?.kWh ?? "";
      rows.push(`${ts},${b},${a}`);
    });
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `campaign_overlay.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const openPdf = () => { if (selected) window.open(`${API}/conversions/${selected}/report.pdf`, "_blank"); };

  const deleteConversion = async (cid) => {
    console.log("[DELETE] Attempting to delete conversion:", cid);
    try { 
      await apiClient.delete(`/conversions/${cid}`); 
      setItems((prev)=>prev.filter(c=>c.conversionId!==cid)); 
      if (selected===cid){setSelected(null); setResults(null); setOverlay({before:[], after:[]});} 
      toast.success("Conversion deleted"); 
    } catch (e) { 
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || "Delete failed";
      console.error("[DELETE] Error:", {
        status,
        msg,
        conversionId: cid,
        responseData: e?.response?.data,
        fullError: e
      });
      
      // If 404, the conversion doesn't exist - try debug endpoint first
      if (status === 404) {
        console.log("[DELETE] 404 received - checking debug endpoint...");
        try {
          const debugRes = await apiClient.get(`/conversions/debug/check/${cid}`);
          console.log("[DELETE] Debug check result:", debugRes.data);
        } catch (debugErr) {
          console.log("[DELETE] Debug check failed:", debugErr);
        }
        
        setItems((prev)=>prev.filter(c=>c.conversionId!==cid));
        if (selected===cid){setSelected(null); setResults(null); setOverlay({before:[], after:[]});}
        toast.error(`${msg} - Check console for details`);
        // Refresh the conversions list from server
        try {
          const res = await apiClient.get("/conversions");
          console.log("[DELETE] Refreshed conversions list:", res.data?.length, "items");
          setItems(res.data || []);
        } catch (refreshErr) {
          console.error('[DELETE] Failed to refresh conversions:', refreshErr);
        }
      } else {
        toast.error(msg);
      }
    }
  };

  const sendEmailPdf = async () => {
    if (!emailRecipient || !emailRecipientName) {
      toast.error("Please fill in recipient details");
      return;
    }
    try {
      await apiClient.post(`/conversions/${selected}/email-report`, {
        recipient_email: emailRecipient,
        recipient_name: emailRecipientName,
        sender_name: emailSenderName,
        message: emailMessage
      });
      toast.success(`Report sent to ${emailRecipient}`);
      setEmailDialogOpen(false);
      setEmailRecipient("");
      setEmailRecipientName("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    }
  };

  const openEditDialog = (conversion) => {
    setEditingConversion(conversion);
    setName(conversion.name);
    setSelectedDevice(conversion.devices[0]);
    setBeforeStart(new Date(conversion.before.start));
    setBeforeEnd(new Date(conversion.before.end));
    setAfterStart(new Date(conversion.after.start));
    setAfterEnd(new Date(conversion.after.end));
    setEditDialogOpen(true);
  };

  const updateConversion = async () => {
    if (!selectedDevice) return toast.error("Select a device");
    const body = {
      siteId: "SITE-ACME-A",
      name,
      devices: [selectedDevice],
      before: { start: new Date(beforeStart).toISOString(), end: new Date(beforeEnd).toISOString() },
      after: { start: new Date(afterStart).toISOString(), end: new Date(afterEnd).toISOString() },
      notes: "",
    };
    try {
      const res = await apiClient.put(`/conversions/${editingConversion.conversionId}`, body);
      toast.success("Conversion updated");
      setItems((prev) => prev.map(c => c.conversionId === editingConversion.conversionId ? res.data : c));
      setEditDialogOpen(false);
      setEditingConversion(null);
      // Reset form
      setName("New Conversion");
      setBeforeStart(new Date());
      setBeforeEnd(new Date());
      setAfterStart(new Date());
      setAfterEnd(new Date());
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  // Prepare comparison chart data - USE UNIFIED ENDPOINT DATA
  const comparisonChartData = useMemo(() => {
    // If we have pre-computed chart data from the unified endpoint, use it directly
    if (overlay.chartData && overlay.chartData.length > 0) {
      console.log(`[Chart] Using unified chart data: ${overlay.chartData.length} points`);
      const chartData = overlay.chartData.map(d => ({
        time: d.label,
        before: d.before_kWh,
        after: d.after_kWh,
        beforeTs: d.before_ts || '',
        afterTs: d.after_ts || '',
      }));
      
      // Filter out points where BOTH before and after are null (sparse data)
      // Keep points where at least one value exists
      const filteredData = chartData.filter(d => d.before !== null || d.after !== null);
      
      // If we have less than 3 data points total, return all to show something
      if (filteredData.length < 3) {
        console.log(`[Chart] Sparse data, showing all ${chartData.length} points`);
        return chartData;
      }
      
      console.log(`[Chart] Filtered to ${filteredData.length} points with data`);
      return filteredData;
    }
    
    // Fallback: compute locally from overlay.before/after if needed
    if (!results || (!overlay.before.length && !overlay.after.length)) return [];
    
    // Get the time periods from the conversion
    const beforeStart = new Date(results.before.start);
    const beforeEnd = new Date(results.before.end);
    const afterStart = new Date(results.after.start);
    const afterEnd = new Date(results.after.end);
    
    // Calculate hours for each period
    const beforeHours = Math.ceil((beforeEnd - beforeStart) / (1000 * 60 * 60));
    const afterHours = Math.ceil((afterEnd - afterStart) / (1000 * 60 * 60));
    const maxHours = Math.max(beforeHours, afterHours, 1);
    
    // Create maps to lookup data by hour offset
    const beforeByHour = new Map();
    const afterByHour = new Map();
    
    // Helper to parse timestamp and get hour index
    const getHourOffset = (ts, periodStart) => {
      if (!ts) return -1;
      let pointTime;
      if (ts.length === 13) {
        pointTime = new Date(ts + ':00:00Z');
      } else if (ts.length === 10) {
        pointTime = new Date(ts + 'T00:00:00Z');
      } else {
        pointTime = new Date(ts);
      }
      if (isNaN(pointTime.getTime())) return -1;
      const periodStartHour = new Date(periodStart);
      periodStartHour.setMinutes(0, 0, 0);
      return Math.floor((pointTime - periodStartHour) / (1000 * 60 * 60));
    };
    
    // Map data to hour indices
    overlay.before.forEach(point => {
      const hourOffset = getHourOffset(point.ts, beforeStart);
      if (hourOffset >= 0 && hourOffset <= beforeHours) {
        beforeByHour.set(hourOffset, point);
      }
    });
    
    overlay.after.forEach(point => {
      const hourOffset = getHourOffset(point.ts, afterStart);
      if (hourOffset >= 0 && hourOffset <= afterHours) {
        afterByHour.set(hourOffset, point);
      }
    });
    
    // Generate chart data with dynamic labels based on duration
    // If period > 48 hours, show "D1 H0" format, otherwise just "Hour X"
    const useDayFormat = maxHours > 48;
    
    const chartData = [];
    for (let i = 0; i <= maxHours; i++) {
      const beforePoint = beforeByHour.get(i);
      const afterPoint = afterByHour.get(i);
      
      // Generate label based on duration
      let label;
      if (useDayFormat) {
        const day = Math.floor(i / 24);
        const hourOfDay = i % 24;
        label = `D${day + 1} H${hourOfDay}`;
      } else {
        label = `Hour ${i}`;
      }
      
      if (beforePoint || afterPoint || i < maxHours) {
        chartData.push({
          time: label,
          before: beforePoint?.kWh ?? null,
          after: afterPoint?.kWh ?? null,
          beforeTs: beforePoint?.ts || '',
          afterTs: afterPoint?.ts || '',
        });
      }
    }
    
    console.log(`[Chart] Fallback: Before ${beforeByHour.size}/${overlay.before.length}, After ${afterByHour.size}/${overlay.after.length}`);
    return chartData;
  }, [overlay, results]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <header className="border-b bg-white/80 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-blue-600 to-emerald-600 flex items-center justify-center flex-shrink-0">
                <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Conversion Management</h1>
                <p className="text-xs text-gray-500 hidden sm:block">Energy comparison analysis</p>
              </div>
            </Link>
            <nav className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <Link className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors" to="/" data-testid="nav-overview-link">Overview</Link>
              <Link className="hidden md:block text-sm font-medium text-blue-600" to="/conversions" data-testid="nav-conversions-link">Conversions</Link>
              <Link className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors" to="/admin" data-testid="nav-admin-link">Admin</Link>
              <Link className="md:hidden text-gray-700 hover:text-blue-600" to="/" title="Overview">
                <BarChart3 className="w-5 h-5" />
              </Link>
              <Link className="md:hidden text-gray-700 hover:text-blue-600" to="/admin" title="Admin">
                <Settings className="w-5 h-5" />
              </Link>
              <LogoutButton />
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-4 sm:space-y-6" data-testid="conversions-page">
        <Card className="p-6 bg-white border-gray-200" data-testid="conversion-wizard">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Conversion</h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Conversion Name</label>
                <Input value={name} onChange={(e)=>setName(e.target.value)} data-testid="conversion-name-input" placeholder="Enter conversion name" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Select Device</label>
                <Select value={selectedDevice} onValueChange={setSelectedDevice}>
                  <SelectTrigger className="w-full" data-testid="device-selector">
                    <SelectValue placeholder="Choose a device" />
                  </SelectTrigger>
                  <SelectContent>
                    {devices.map((d) => (
                      <SelectItem key={d.deviceId} value={d.deviceId} data-testid={`device-option-${d.deviceId}`}>
                        {d.deviceId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">Before Window</label>
              <div className="grid grid-cols-2 gap-3">
                <DateTimePicker label="Start" value={beforeStart} onChange={setBeforeStart} testid="before-start" />
                <DateTimePicker label="End" value={beforeEnd} onChange={setBeforeEnd} testid="before-end" />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">After Window</label>
              <div className="grid grid-cols-2 gap-3">
                <DateTimePicker label="Start" value={afterStart} onChange={setAfterStart} testid="after-start" />
                <DateTimePicker label="End" value={afterEnd} onChange={setAfterEnd} testid="after-end" />
              </div>
            </div>
          </div>
          <div className="mt-6">
            <Button onClick={submitConversion} data-testid="create-conversion-btn" className="px-6">
              Create Conversion
            </Button>
          </div>
        </Card>

        <Card className="p-6 bg-white border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Existing Conversions</h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Conversion ID</TableHead>
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Devices</TableHead>
                  <TableHead className="font-semibold">Before Period</TableHead>
                  <TableHead className="font-semibold">After Period</TableHead>
                  <TableHead className="font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.conversionId} data-testid={`conversion-row-${c.conversionId}`}>
                    <TableCell className="font-mono text-xs">{c.conversionId}</TableCell>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{(c.devices || []).join(", ")}</TableCell>
                    <TableCell className="text-sm">{formatDateTime(c.before?.start)} → {formatDateTime(c.before?.end)}</TableCell>
                    <TableCell className="text-sm">{formatDateTime(c.after?.start)} → {formatDateTime(c.after?.end)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-nowrap">
                        <Button size="sm" onClick={() => { setSelected(c.conversionId); runCompare(c.conversionId); }} data-testid={`conversion-compare-button-${c.conversionId}`} className="gap-1">
                          <BarChart3 className="w-4 h-4" />
                          Compare
                        </Button>
                        {canModifyConversion(c) && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => openEditDialog(c)} data-testid={`conversion-edit-button-${c.conversionId}`} className="gap-1">
                              <Edit className="w-3 h-3" />
                              Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => deleteConversion(c.conversionId)} data-testid={`conversion-delete-button-${c.conversionId}`} className="gap-1 text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        {results && (
          <Card className="p-6 bg-white border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Comparison Results</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={exportOverlayCsv} data-testid="export-overlay-csv">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button variant="outline" size="sm" onClick={openPdf} data-testid="export-pdf">
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
                <Button size="sm" onClick={() => setEmailDialogOpen(true)} data-testid="email-pdf-btn" className="bg-blue-600 hover:bg-blue-700">
                  <Mail className="w-4 h-4 mr-2" />
                  Email PDF
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {results.perDevice?.map((pd) => (
                <Card key={pd.deviceId} className="p-4 border-gray-200">
                  <p className="text-sm font-semibold text-gray-700 mb-3">{pd.deviceId}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Before:</span>
                      <span className="font-semibold">{(pd.kWh_before || 0).toFixed(2)} kWh</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">After:</span>
                      <span className="font-semibold">{(pd.kWh_after || 0).toFixed(2)} kWh</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-gray-600">Savings:</span>
                      <span className={`font-bold ${(pd.delta_kWh || 0) < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {(pd.delta_kWh || 0).toFixed(2)} kWh {pd.percent_change != null ? `(${pd.percent_change.toFixed(1)}%)` : '(N/A)'}
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-6">
              <h4 className="text-center text-xl font-semibold text-gray-800 mb-4">Energy Consumption</h4>
              
              {/* Show warning if chart data is sparse or missing - check if any actual data points exist */}
              {results && (() => {
                // Check if chart has any actual data points (not all nulls)
                const hasBeforeData = comparisonChartData.some(d => d.before != null);
                const hasAfterData = comparisonChartData.some(d => d.after != null);
                
                // Get totals from results (same source as stat cards) for edge cases
                const beforeTotal = results.perDevice?.reduce((sum, pd) => sum + (pd.kWh_before || 0), 0) || 0;
                const afterTotal = results.perDevice?.reduce((sum, pd) => sum + (pd.kWh_after || 0), 0) || 0;
                
                if (!hasBeforeData && !hasAfterData) {
                  return (
                    <div data-testid="missing-all-warning" className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 text-center">
                      <p className="text-yellow-800 font-medium">No hourly energy data available for chart</p>
                      <p className="text-yellow-600 text-sm mt-1">
                        {beforeTotal > 0 || afterTotal > 0 
                          ? "Energy totals are shown above but detailed hourly data is not available." 
                          : "The selected time ranges may not have recorded data."}
                      </p>
                    </div>
                  );
                } else if (!hasBeforeData && hasAfterData) {
                  return (
                    <div data-testid="missing-before-warning" className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-center">
                      <p className="text-amber-800 font-medium">No "Before" period chart data available</p>
                      <p className="text-amber-600 text-sm mt-1">Only the "After" period has hourly chart data.</p>
                    </div>
                  );
                } else if (hasBeforeData && !hasAfterData) {
                  return (
                    <div data-testid="missing-after-warning" className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-center">
                      <p className="text-amber-800 font-medium">No "After" period chart data available</p>
                      <p className="text-amber-600 text-sm mt-1">Only the "Before" period has hourly chart data.</p>
                    </div>
                  );
                }
                return null;
              })()}
              
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={comparisonChartData} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE_COLOR} />
                  <XAxis 
                    dataKey="time" 
                    stroke={AXIS_STROKE_COLOR}
                    tick={AXIS_TICK_STYLE}
                  />
                  <YAxis 
                    stroke={AXIS_STROKE_COLOR}
                    tick={AXIS_TICK_STYLE}
                    label={{ value: 'kWh', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#6b7280' } }}
                  />
                  <Tooltip 
                    contentStyle={TOOLTIP_STYLE}
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const data = payload[0].payload;
                      return (
                        <div style={{ padding: '10px', fontSize: '12px' }}>
                          <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>{data.time}</p>
                          {data.before !== null && (
                            <div style={{ color: '#1e3a8a', marginBottom: '3px' }}>
                              <strong>Before:</strong> {data.before.toFixed(2)} kWh
                              {data.beforeTs && (
                                <>
                                  <br />
                                  <span style={{ fontSize: '10px', color: '#6b7280' }}>{data.beforeTs}</span>
                                </>
                              )}
                            </div>
                          )}
                          {data.after !== null && (
                            <div style={{ color: '#ea580c' }}>
                              <strong>After:</strong> {data.after.toFixed(2)} kWh
                              {data.afterTs && (
                                <>
                                  <br />
                                  <span style={{ fontSize: '10px', color: '#6b7280' }}>{data.afterTs}</span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="before" 
                    stroke="#1e3a8a" 
                    strokeWidth={2.5}
                    name="Before"
                    dot={false}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="after" 
                    stroke="#ea580c" 
                    strokeWidth={2.5}
                    name="After"
                    dot={false}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="square"
                    align="center"
                    verticalAlign="bottom"
                    layout="horizontal"
                    content={(props) => {
                      const { payload } = props;
                      if (!payload) return null;
                      // Reverse the order to show Before first
                      const reversedPayload = [...payload].reverse();
                      return (
                        <ul style={{ display: 'flex', justifyContent: 'center', listStyle: 'none', padding: 0, margin: '20px 0 0 0' }}>
                          {reversedPayload.map((entry, index) => (
                            <li key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', marginRight: index === 0 ? '20px' : '0' }}>
                              <svg width="14" height="14" style={{ marginRight: '5px' }}>
                                <rect width="14" height="14" fill={entry.color} />
                              </svg>
                              <span style={{ color: '#374151', fontSize: '12px' }}>{entry.value}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
              </div>
            </div>
          </Card>
        )}

        {/* Edit Conversion Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-4xl" data-testid="edit-dialog">
            <DialogHeader>
              <DialogTitle>Edit Conversion</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <div>
                  <Label>Conversion Name</Label>
                  <Input value={name} onChange={(e)=>setName(e.target.value)} data-testid="edit-conversion-name" placeholder="Enter conversion name" />
                </div>
                <div>
                  <Label>Select Device</Label>
                  <Select value={selectedDevice} onValueChange={setSelectedDevice}>
                    <SelectTrigger className="w-full" data-testid="edit-device-selector">
                      <SelectValue placeholder="Choose a device" />
                    </SelectTrigger>
                    <SelectContent>
                      {devices.map((d) => (
                        <SelectItem key={d.deviceId} value={d.deviceId}>
                          {d.deviceId}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="block mb-2">Before Window</Label>
                <div className="grid grid-cols-2 gap-3">
                  <DateTimePicker label="Start" value={beforeStart} onChange={setBeforeStart} testid="edit-before-start" />
                  <DateTimePicker label="End" value={beforeEnd} onChange={setBeforeEnd} testid="edit-before-end" />
                </div>
              </div>
              <div>
                <Label className="block mb-2">After Window</Label>
                <div className="grid grid-cols-2 gap-3">
                  <DateTimePicker label="Start" value={afterStart} onChange={setAfterStart} testid="edit-after-start" />
                  <DateTimePicker label="End" value={afterEnd} onChange={setAfterEnd} testid="edit-after-end" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditingConversion(null); }}>Cancel</Button>
              <Button onClick={updateConversion} data-testid="update-conversion-btn">
                Update Conversion
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email PDF Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="email-dialog">
            <DialogHeader>
              <DialogTitle>Email PDF Report</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Recipient Email *</Label>
                  <Input 
                    type="email"
                    value={emailRecipient} 
                    onChange={(e) => setEmailRecipient(e.target.value)}
                    placeholder="customer@company.com"
                    data-testid="email-recipient"
                  />
                </div>
                <div>
                  <Label>Recipient Name *</Label>
                  <Input 
                    value={emailRecipientName} 
                    onChange={(e) => setEmailRecipientName(e.target.value)}
                    placeholder="John Doe"
                    data-testid="email-recipient-name"
                  />
                </div>
              </div>
              <div>
                <Label>Your Name</Label>
                <Input 
                  value={emailSenderName} 
                  onChange={(e) => setEmailSenderName(e.target.value)}
                  placeholder="DST-CHEMICALS Team"
                  data-testid="email-sender-name"
                />
              </div>
              <div>
                <Label>Message (will be included in email)</Label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  className="w-full min-h-32 p-3 border rounded-md text-sm"
                  placeholder="Enter your message..."
                  data-testid="email-message"
                />
                <p className="text-xs text-gray-500 mt-1">This message will appear in the email body alongside the PDF attachment.</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">📧 Email Preview</h4>
                <p className="text-xs text-blue-700">The recipient will receive a professionally formatted email with:</p>
                <ul className="text-xs text-blue-600 list-disc list-inside mt-1 space-y-1">
                  <li>Company branding and header</li>
                  <li>Report information (name, date, sender)</li>
                  <li>Your custom message</li>
                  <li>PDF report as attachment</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
              <Button onClick={sendEmailPdf} data-testid="send-email-btn">
                <Mail className="w-4 h-4 mr-2" />
                Send Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

function Admin() {
  const api = useApi();
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [threshold, setThreshold] = useState(2);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserName, setNewUserName] = useState("");
  const [newUserRole, setNewUserRole] = useState("user");
  const [editingDevice, setEditingDevice] = useState(null);
  const [deviceColors, setDeviceColors] = useState({});
  const [logoFile, setLogoFile] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [settingsRes, usersRes, devicesRes] = await Promise.all([
          api.get('/settings'), 
          api.get('/users'),
          api.get('/devices/all')
        ]);
        setThreshold(settingsRes.data.offlineThresholdMinutes || 2);
        setUsers(usersRes.data || []);
        setDevices(devicesRes.data || []);
        setDeviceColors(settingsRes.data.deviceColors || {});
      } catch (err) {
        console.error('Failed to load admin settings:', err);
      }
    })();
  }, [api]);

  const changePassword = async () => {
    try {
      await api.post('/auth/change-password', { currentPassword: currentPass, newPassword: newPass });
      toast.success("Password updated");
      setCurrentPass(""); setNewPass("");
    } catch (err) { 
      console.error('Failed to change password:', err);
      toast.error("Failed to change password"); 
    }
  };

  const updateThreshold = async () => {
    try {
      await api.put('/settings', { offlineThresholdMinutes: threshold });
      toast.success("Settings updated");
    } catch (err) { 
      console.error('Failed to update threshold:', err);
      toast.error("Update failed"); 
    }
  };

  const createUser = async () => {
    try {
      const res = await api.post('/users', { email: newUserEmail, name: newUserName, role: newUserRole });
      setUsers([...users, res.data]);
      toast.success(`User created! Invitation email sent to ${newUserEmail}`);
      setNewUserEmail(""); setNewUserName(""); setNewUserRole("user");
    } catch (e) { 
      toast.error(e?.response?.data?.detail || "Create failed"); 
    }
  };

  const updateUserRole = async (email, role) => {
    try {
      await api.put(`/users/${email}`, { role });
      setUsers(users.map(u => u.email === email ? { ...u, role } : u));
      toast.success("Role updated");
    } catch (err) { 
      console.error('Failed to update user role:', err);
      toast.error("Update failed"); 
    }
  };

  const resetUserPassword = async (email) => {
    try {
      const res = await api.post(`/users/${email}/reset-password`);
      toast.success(`Temp password: ${res.data.tempPassword}`);
    } catch (err) { 
      console.error('Failed to reset user password:', err);
      toast.error("Reset failed"); 
    }
  };

  const deleteUser = async (email) => {
    try {
      await api.delete(`/users/${email}`);
      setUsers(users.filter(u => u.email !== email));
      toast.success("User deleted");
    } catch (e) { 
      toast.error(e?.response?.data?.detail || "Delete failed"); 
    }
  };

  const updateDeviceName = async (deviceId, displayName) => {
    try {
      await api.put(`/devices/${deviceId}`, { displayName });
      setDevices(devices.map(d => d.deviceId === deviceId ? { ...d, displayName } : d));
      setEditingDevice(null);
      toast.success("Device name updated");
    } catch { toast.error("Update failed"); }
  };

  const toggleDeviceActive = async (deviceId, active) => {
    try {
      await api.put(`/devices/${deviceId}`, { active });
      setDevices(devices.map(d => d.deviceId === deviceId ? { ...d, active } : d));
      toast.success(`Device ${active ? 'activated' : 'deactivated'}`);
    } catch { toast.error("Update failed"); }
  };

  const updateDeviceColors = async () => {
    try {
      await api.put('/settings', { deviceColors });
      toast.success("Device colors updated");
    } catch { toast.error("Update failed"); }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      await api.post('/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Logo uploaded successfully");
      setLogoFile(null);
    } catch { toast.error("Upload failed"); }
  };

  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  
  const uploadHistoricalData = async () => {
    if (!csvFile) {
      toast.error("Please select a CSV file first");
      return;
    }
    
    try {
      setCsvUploading(true);
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await api.post('/admin/ingest-historical-data', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000 // 5 minute timeout for large files
      });
      
      toast.success(
        `✅ Successfully ingested ${response.data.inserted.toLocaleString()} readings! ` +
        `(Converted to Watts and stored) - Skipped: ${response.data.skipped}, Errors: ${response.data.errors}`,
        { duration: 12000 }
      );
      setCsvFile(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Upload failed");
    } finally {
      setCsvUploading(false);
    }
  };

  const [bulkUserFile, setBulkUserFile] = useState(null);
  const [bulkUserUploading, setBulkUserUploading] = useState(false);
  const [bulkUserResult, setBulkUserResult] = useState(null);

  const bulkInviteUsers = async () => {
    if (!bulkUserFile) {
      toast.error("Please select a CSV file first");
      return;
    }

    try {
      setBulkUserUploading(true);
      setBulkUserResult(null);
      const formData = new FormData();
      formData.append('file', bulkUserFile);

      const response = await api.post('/users/bulk-invite', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 minute timeout
      });

      setBulkUserResult(response.data);
      
      // Refresh user list
      const usersRes = await api.get('/users');
      setUsers(usersRes.data || []);

      toast.success(
        `✅ Bulk invitation complete! Created: ${response.data.created}, Skipped: ${response.data.skipped}, Failed: ${response.data.failed}`,
        { duration: 8000 }
      );
      
      setBulkUserFile(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Bulk invitation failed");
      console.error("Bulk invite error:", error);
    } finally {
      setBulkUserUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <header className="border-b bg-white/80 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-blue-600 to-emerald-600 flex items-center justify-center flex-shrink-0">
                <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Administration</h1>
                <p className="text-xs text-gray-500 hidden sm:block">System settings & user management</p>
              </div>
            </Link>
            <nav className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <Link className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors" to="/" data-testid="nav-overview-link">Overview</Link>
              <Link className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors" to="/conversions" data-testid="nav-conversions-link">Conversions</Link>
              <Link className="hidden md:block text-sm font-medium text-blue-600" to="/admin" data-testid="nav-admin-link">Admin</Link>
              <Link className="md:hidden text-gray-700 hover:text-blue-600" to="/" title="Overview">
                <BarChart3 className="w-5 h-5" />
              </Link>
              <Link className="md:hidden text-gray-700 hover:text-blue-600" to="/conversions" title="Conversions">
                <CalendarIcon className="w-5 h-5" />
              </Link>
              <LogoutButton />
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-4 sm:space-y-6" data-testid="admin-page">
        <Card className="p-6 bg-white border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Change Password
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">Current Password</label>
              <Input type="password" value={currentPass} onChange={(e) => setCurrentPass(e.target.value)} data-testid="current-password" />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">New Password</label>
              <Input type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} data-testid="new-password" />
            </div>
            <div className="flex items-end">
              <Button onClick={changePassword} data-testid="change-password-btn">Update Password</Button>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Device Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">Offline Threshold (minutes)</label>
              <Input type="number" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} data-testid="threshold-input" />
              <p className="text-xs text-gray-500 mt-1">Devices will be marked offline after this many minutes of inactivity</p>
            </div>
            <div className="flex items-end">
              <Button onClick={updateThreshold} data-testid="update-threshold-btn">Update Settings</Button>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Company Logo
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">Upload Logo for PDF Reports</label>
              <Input 
                type="file" 
                accept="image/*"
                onChange={(e) => setLogoFile(e.target.files[0])} 
              />
              <p className="text-xs text-gray-500 mt-1">Logo will appear in PDF reports. Recommended: 200x200px PNG</p>
            </div>
            <div className="flex items-end">
              <Button onClick={uploadLogo} disabled={!logoFile}>
                Upload Logo
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border-gray-200">

        <Card className="p-6 bg-white border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Import Historical Data
          </h3>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">📄 CSV File Format</p>
              <p className="text-xs text-blue-700 mb-2">Semicolon-delimited, no headers. Expected format:</p>
              <code className="text-xs bg-white px-2 py-1 rounded border border-blue-200 block">
                RecordID;LogTime;MeasurementTime;DeviceID;PhaseA_Power;PhaseB_Power;PhaseC_Power
              </code>
              <p className="text-xs text-blue-600 mt-2">Example:</p>
              <code className="text-xs bg-white px-2 py-1 rounded border border-blue-200 block">
                2202736;2025-12-02 07:56:11.600;2025-11-30 12:10:00.000;DST001;5412.3;5608.2;5437.0
              </code>
              <p className="text-xs text-blue-800 mt-3 font-semibold">⚡ Power Conversion:</p>
              <p className="text-xs text-blue-700">
                Power values will be automatically divided by 10,000 to convert to Watts
                <br />
                Example: 5412.3 → 0.54 W (5412.3 ÷ 10000)
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Select CSV File</label>
                <Input 
                  type="file" 
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  disabled={csvUploading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {csvFile ? `Selected: ${csvFile.name} (${(csvFile.size / 1024 / 1024).toFixed(2)} MB)` : 'Large files supported (150MB+, 1.7M+ records)'}
                </p>
              </div>
              <div className="flex items-end">
                <Button 
                  onClick={uploadHistoricalData} 
                  disabled={!csvFile || csvUploading}
                  className="w-full"
                >
                  {csvUploading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Uploading... (This may take a few minutes)
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload & Ingest Data
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800 mb-2">
                ⚠️ <strong>Important Notes:</strong>
              </p>
              <ul className="text-xs text-yellow-800 space-y-1 ml-4 list-disc">
                <li>Power values will be divided by 10,000 to convert to Watts</li>
                <li>Negative values will be converted to positive automatically</li>
                <li>Only valid data matching the database structure will be imported</li>
                <li>Invalid rows will be skipped and reported</li>
                <li>Upload may take several minutes for large files - do not close this page</li>
              </ul>
            </div>
          </div>
        </Card>

          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Device Management
          </h3>
          
          <div className="overflow-x-auto mb-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Device ID</TableHead>
                  <TableHead className="font-semibold">Display Name</TableHead>
                  <TableHead className="font-semibold">Chart Color</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.map((device) => (
                  <TableRow key={device.deviceId}>
                    <TableCell className="font-medium">{device.deviceId}</TableCell>
                    <TableCell>
                      {editingDevice === device.deviceId ? (
                        <div className="flex gap-2">
                          <Input
                            value={device.displayName}
                            onChange={(e) => setDevices(devices.map(d => 
                              d.deviceId === device.deviceId ? { ...d, displayName: e.target.value } : d
                            ))}
                            className="w-40"
                          />
                          <Button size="sm" onClick={() => updateDeviceName(device.deviceId, device.displayName)}>
                            Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingDevice(null)}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span>{device.displayName}</span>
                          <Button size="sm" variant="ghost" onClick={() => setEditingDevice(device.deviceId)}>
                            <Edit className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <input
                        type="color"
                        value={deviceColors[device.deviceId] || '#3b82f6'}
                        onChange={(e) => setDeviceColors({...deviceColors, [device.deviceId]: e.target.value})}
                        className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={device.active !== false}
                        onCheckedChange={(checked) => toggleDeviceActive(device.deviceId, checked)}
                      />
                      <span className="ml-2 text-sm text-gray-600">
                        {device.active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-gray-500">
                        Last seen: {device.lastSeen ? formatRelative(device.lastSeen) : 'Never'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          <div className="flex justify-end">
            <Button onClick={updateDeviceColors}>
              Save Color Preferences
            </Button>
          </div>
        </Card>

        <Card className="p-6 bg-white border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            User Management
          </h3>
          
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Create New User</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Input placeholder="Email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} data-testid="new-user-email" />
              <Input placeholder="Name" value={newUserName} onChange={(e) => setNewUserName(e.target.value)} data-testid="new-user-name" />
              <Select value={newUserRole} onValueChange={setNewUserRole}>
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={createUser} data-testid="create-user-btn">Create User</Button>
            </div>
          </div>

          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Bulk User Invitation
            </h4>
            <div className="space-y-3">
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <p className="text-xs font-semibold text-blue-900 mb-2">📄 CSV File Format</p>
                <p className="text-xs text-blue-700 mb-2">Two columns required (case-insensitive):</p>
                <code className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-300 block">
                  Name,Email
                </code>
                <p className="text-xs text-blue-600 mt-2">Example:</p>
                <code className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-300 block">
                  John Doe,john@example.com<br/>
                  Jane Smith,jane@example.com
                </code>
                <ul className="text-xs text-blue-700 mt-2 space-y-1 ml-4 list-disc">
                  <li>All users created with <strong>"user"</strong> role (not admin)</li>
                  <li>Individual invitation emails sent to each user</li>
                  <li>Existing users will be skipped</li>
                  <li>Invalid emails will be reported</li>
                </ul>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Input 
                    type="file" 
                    accept=".csv"
                    onChange={(e) => setBulkUserFile(e.target.files[0])}
                    disabled={bulkUserUploading}
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    {bulkUserFile ? `Selected: ${bulkUserFile.name}` : 'Select CSV file with Name and Email columns'}
                  </p>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={bulkInviteUsers}
                    disabled={!bulkUserFile || bulkUserUploading}
                    className="w-full md:w-auto"
                  >
                    {bulkUserUploading ? (
                      <>
                        <Upload className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Bulk Invite Users
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {bulkUserResult && (
                <div className={`p-3 rounded-lg border ${
                  bulkUserResult.failed > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'
                }`}>
                  <p className="text-sm font-semibold mb-2">
                    {bulkUserResult.failed > 0 ? '⚠️ Import Complete with Issues' : '✅ Import Successful'}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-2">
                    <div className="bg-white rounded px-2 py-1 border">
                      <span className="font-semibold">Total:</span> {bulkUserResult.total}
                    </div>
                    <div className="bg-green-100 rounded px-2 py-1 border border-green-300">
                      <span className="font-semibold text-green-800">Created:</span> {bulkUserResult.created}
                    </div>
                    <div className="bg-gray-100 rounded px-2 py-1 border border-gray-300">
                      <span className="font-semibold">Skipped:</span> {bulkUserResult.skipped}
                    </div>
                    <div className="bg-red-100 rounded px-2 py-1 border border-red-300">
                      <span className="font-semibold text-red-800">Failed:</span> {bulkUserResult.failed}
                    </div>
                  </div>
                  
                  {bulkUserResult.details.length > 0 && (
                    <div className="max-h-40 overflow-y-auto bg-white rounded border p-2">
                      <p className="text-xs font-semibold mb-1">Details:</p>
                      {bulkUserResult.details.map((detail) => (
                        <div key={detail.email} className={`text-xs py-1 ${
                          detail.status === 'created' ? 'text-green-700' : 
                          detail.status === 'skipped' ? 'text-gray-600' : 
                          'text-red-700'
                        }`}>
                          <span className="font-medium">{detail.email}</span> - {detail.reason}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Role</TableHead>
                  <TableHead className="font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.email} data-testid={`user-row-${u.email}`}>
                    <TableCell className="font-medium">{u.email}</TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={(v) => updateUserRole(u.email, v)}>
                        <SelectTrigger className="w-32" data-testid={`user-role-${u.email}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="user">User</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="space-x-2">
                      <Button size="sm" variant="destructive" onClick={() => deleteUser(u.email)} data-testid={`user-delete-${u.email}`}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </main>
    </div>
  );
}

export default function App() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const api = useApi();

  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then((registration) => {
            console.log('SW registered:', registration);
          })
          .catch((error) => {
            console.log('SW registration failed:', error);
          });
      });
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        console.log('Fetching /api/me...');
        const res = await api.get("/me");
        console.log('/api/me response:', res.data);
        setMe(res.data);
      } catch (err) {
        console.error('/api/me error:', err);
        setMe(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-emerald-50">
        <div className="text-center">
          <Activity className="w-12 h-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Login onLoggedIn={() => window.location.reload()} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
        </Routes>
        <Toaster />
        <InstallPrompt />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Overview />} />
        <Route path="/device/:deviceId" element={<DeviceDetail />} />
        <Route path="/conversions" element={<Conversions />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
      <Toaster />
      <InstallPrompt />
    </BrowserRouter>
  );
}

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const api = useApi();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/request-password-reset', { email });
      setSubmitted(true);
    } catch (e) {
      toast.error("Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50">
        <div className="max-w-md mx-auto pt-32 px-6">
          <Card className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h2>
            <p className="text-gray-600 mb-6">
              If an account exists with <strong>{email}</strong>, you'll receive a password reset link shortly.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Didn't receive the email? Check your spam folder or try again.
            </p>
            <Button onClick={() => navigate('/')} className="w-full">
              Back to Login
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50">
      <div className="max-w-md mx-auto pt-32 px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-600 mb-6 shadow-lg">
            <Activity className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
            Forgot Password
          </h1>
          <p className="text-gray-600 mt-3">Enter your email to reset your password</p>
        </div>

        <Card className="p-8 shadow-xl border-gray-200">
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">Email Address</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>
            <Button disabled={loading} type="submit" className="w-full h-11 font-semibold">
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Login
            </button>
          </div>
        </Card>
      </div>
      <Toaster />
    </div>
  );
}

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [resetting, setResetting] = useState(false);
  const api = useApi();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link");
      setLoading(false);
      return;
    }

    // Verify token
    (async () => {
      try {
        const res = await api.get(`/auth/verify-reset-token/${token}`);
        setUserInfo(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Invalid or expired reset link");
      } finally {
        setLoading(false);
      }
    })();
  }, [token, api]);

  const handleReset = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setResetting(true);
    try {
      await api.post('/auth/reset-password', {
        token,
        password
      });
      toast.success("Password reset successful! Please log in.");
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-emerald-50">
        <div className="text-center">
          <Activity className="w-12 h-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-emerald-50">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Reset Link</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/forgot-password')}>Request New Link</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50">
      <div className="max-w-md mx-auto pt-20 px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-600 mb-6 shadow-lg">
            <Activity className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
            Reset Password
          </h1>
          <p className="text-gray-600 mt-3">Create a new password for your account</p>
        </div>

        <Card className="p-8 shadow-xl border-gray-200">
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900"><strong>Account:</strong> {userInfo?.email}</p>
          </div>

          <form className="space-y-5" onSubmit={handleReset}>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">New Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">Confirm Password</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                required
                minLength={8}
              />
            </div>
            <Button disabled={resetting} type="submit" className="w-full h-11 font-semibold">
              {resetting ? "Resetting Password..." : "Reset Password"}
            </Button>
          </form>
        </Card>
      </div>
      <Toaster />
    </div>
  );
}

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);
  const api = useApi();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link");
      setLoading(false);
      return;
    }

    // Verify token
    (async () => {
      try {
        const res = await api.get(`/auth/verify-invite/${token}`);
        setUserInfo(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Invalid or expired invitation link");
      } finally {
        setLoading(false);
      }
    })();
  }, [token, api]);

  const handleAccept = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setAccepting(true);
    try {
      await api.post('/auth/accept-invite', {
        token,
        password
      });
      toast.success("Account activated! Logging you in...");
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to activate account");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-emerald-50">
        <div className="text-center">
          <Activity className="w-12 h-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-emerald-50">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')}>Go to Login</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50">
      <div className="max-w-md mx-auto pt-20 px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-600 mb-6 shadow-lg">
            <Activity className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
            Welcome to DST Energy
          </h1>
          <p className="text-gray-600 mt-3">Complete your account setup</p>
        </div>

        <Card className="p-8 shadow-xl border-gray-200">
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900"><strong>Name:</strong> {userInfo?.name}</p>
            <p className="text-sm text-blue-900"><strong>Email:</strong> {userInfo?.email}</p>
            <p className="text-sm text-blue-900"><strong>Role:</strong> {userInfo?.role}</p>
          </div>

          <form className="space-y-5" onSubmit={handleAccept}>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">Create Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">Confirm Password</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                required
                minLength={8}
              />
            </div>
            <Button disabled={accepting} type="submit" className="w-full h-11 font-semibold">
              {accepting ? "Activating Account..." : "Activate Account & Sign In"}
            </Button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-4">
            By activating your account, you agree to our terms of service.
          </p>
        </Card>
      </div>
      <Toaster />
    </div>
  );
}